import React, { useLayoutEffect, useEffect, useState } from "react";

import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { showToast } from "../../store/actions";
import { useDispatch } from "react-redux";
import JobService from "../../service/job";
import HistoryTable from "./HistoryTable";

const useStyles = makeStyles((theme) =>
  createStyles({
    gridItem: {
      minHeight: "300px",
    },
    paper: {
      height: "100%",
      padding: "10px 20px",
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px",
      },
    },
  })
);

const artworkHistoryColumns = [
  {
    id: "approve_status",
    label: "Status",
    width: 100,
  },
  {
    id: "description",
    label: "Description",
    width: 200,
  },
  {
    id: "customer_description",
    label: "Customer Comments",
    width: 200,
  },
  {
    id: "user",
    label: "Updated By",
    width: 150,
  },
  { id: "files", label: "Files", width: 300 },
  {
    id: "download",
    label: "Download",
    width: 100,
    icon: "Download",
  },
];

const ViewJob = ({
  job_id,
  setValue,
  jobName,
  jobDescription,
  category,
  subcategory,
  productLink,
  categoryProductLink,
  images,
  DeliveryFormats,
  role,
  status,
  amount,
  orderDescription,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [boxLayoutHeight, setBoxLayoutHeight] = useState(0);
  const [deliveryFormatsList, setDeliveryFormatsList] = useState(DeliveryFormats);
  const [loading, setLoading] = useState(false);
  const [artworkHistory, setArtworkHistory] = useState([]);
  const [artworkHistoryLoading, setArtworkHistoryLoading] = useState(false);

  useLayoutEffect(() => {
    function updateSize() {
      setBoxLayoutHeight(window.innerHeight - 150);
    }
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    setArtworkHistoryLoading(true);

    const Job = new JobService();
    Job.getHistory(job_id, "Artwork")
      .then((response) => {
        if (response.success) setArtworkHistory(response.data);

        setArtworkHistoryLoading(false);
      })
      .catch(() => {
        setArtworkHistoryLoading(false);
      });
  }, [job_id]);

  const FilesContainer = () => {
    let imageFiles = [];
    let otherFiles = [];

    images.map((image) => {
      if (image.lastIndexOf(".") > 0) {
        const fileType = image.substring(image.lastIndexOf(".") + 1, image.length).toLowerCase();

        if (fileType === "jpg" || fileType === "png") imageFiles.push(image);
        else otherFiles.push(image);
      }
    });

    return (
      <>
        <h3 className={classes.detailsHeader}>
          <b> Image Files </b>
        </h3>

        <ImageList sx={{ width: "100%", maxHeight: "500px" }} cols={2} rowHeight={100}>
          {imageFiles.map((image) => (
            <ImageListItem key={image.split("/").pop()}>
              <img
                style={{ height: "100%", width: "100%", objectFit: "cover" }}
                crossOrigin="anonymous"
                alt="Job"
                src={
                  image
                    ? process.env.REACT_APP_API_URL + "/" + image
                    : process.env.REACT_APP_API_URL + "/file/jobs/NoImageAvailable.png"
                }
                loading="lazy"
              />
            </ImageListItem>
          ))}
        </ImageList>

        <h3 className={classes.detailsHeader}>
          <b> Other Files </b>
        </h3>

        <aside>
          <ul style={{ marginTop: 0 }}>
            {otherFiles.map((file) => (
              <li key={file}>{file.split("/").pop()}</li>
            ))}
          </ul>
        </aside>

        <Button
          variant="contained"
          size="small"
          color="info"
          onClick={() => {
            imageFiles.forEach((image) => window.open(process.env.REACT_APP_API_URL + "/" + image, "_blank"));
            otherFiles.forEach((file) => window.open(process.env.REACT_APP_API_URL + "/" + file, "_blank"));
          }}
          disabled={imageFiles.length === 0 && otherFiles.length === 0}
        >
          Download all files
        </Button>
      </>
    );
  };

  const handleDeliveryFormatChange = (value, index) => {
    setDeliveryFormatsList((prev) =>
      prev.map((deliveryFormat, dfIndex) => {
        if (index == dfIndex) deliveryFormat.value = value;
        return deliveryFormat;
      })
    );
  };

  const handleDevliveryFormatSave = async () => {
    const finalDeliveryFormats = deliveryFormatsList.map((data) => ({
      id: data.id,
      name: data.text,
      value: data.value,
    }));

    setLoading(true);
    const jobService = new JobService();

    const submitData = new FormData();
    submitData.append("DeliveryFormats", JSON.stringify(finalDeliveryFormats));

    // Update Job
    const response = await jobService.updateJob(job_id, submitData);

    if (response.success) {
      dispatch(showToast({ message: "Delivery Format updated successfully.", type: "success" }));
    } else {
      dispatch(showToast({ message: "Error in updating Delivery Format.", type: "success" }));
    }

    setLoading(false);
  };

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box
          sx={{
            overflowY: "auto",
            height: `${boxLayoutHeight}px`,
            maxHeight: `${boxLayoutHeight}px`,
          }}
        >
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={12} md={12} lg={4} xl={4} className={classes.gridItem}>
              <Paper className={classes.paper}>
                <FilesContainer />
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={4} xl={4} className={classes.gridItem}>
              <Paper className={classes.paper}>
                <h3 className={classes.detailsHeader}>
                  <b> Job Name & Instructions </b>
                </h3>
                <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}>
                  <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                    <ListItemText primary="Job Name" secondary={jobName} />
                  </ListItem>

                  {category && (
                    <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                      <ListItemText primary="Category" secondary={category} />
                    </ListItem>
                  )}

                  {subcategory && (
                    <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                      <ListItemText primary="Sub Category" secondary={subcategory} />
                    </ListItem>
                  )}

                  <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                    <ListItemText primary="Job Description" secondary={jobDescription} />
                  </ListItem>

                  {categoryProductLink == 1 && (
                    <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                      <ListItemText primary="Product Link" secondary={productLink} />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={4} xl={4} className={classes.gridItem}>
              <Paper className={classes.paper}>
                <h3 className={classes.detailsHeader}>
                  <b> Delivery Formats </b>
                </h3>

                {role !== 3 ? (
                  deliveryFormatsList.length > 0 && (
                    <>
                      {deliveryFormatsList.map((dlf, dlfIndex) => (
                        <Box key={dlf.id} sx={{ mt: 1 }}>
                          <InputLabel style={{ fontSize: "14px" }} sx={{ mt: 1 }} className="inputFormCustomLabel">
                            {" "}
                            {dlf.text}{" "}
                          </InputLabel>
                          {dlf.type === "text" ? (
                            <TextField
                              margin="normal"
                              value={dlf.value}
                              onChange={(e) => handleDeliveryFormatChange(e.target.value, dlfIndex)}
                              fullWidth
                              variant="filled"
                              size="small"
                              hiddenLabel
                              sx={{ mt: 1 }}
                            />
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <MobileDatePicker
                                value={dlf.value}
                                disablePast
                                onChange={(date) => {
                                  handleDeliveryFormatChange(
                                    date ? new Date(date).toISOString().slice(0, 19).replace("T", " ") : "",
                                    dlfIndex
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    margin="normal"
                                    sx={{ mt: 1, cursor: "pointer", input: { cursor: "pointer" } }}
                                    fullWidth
                                    variant="filled"
                                    size="small"
                                    hiddenLabel
                                    {...params}
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          )}
                        </Box>
                      ))}

                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 1.5 }}
                        onClick={() => handleDevliveryFormatSave()}
                        style={{
                          backgroundColor: "#3a3d4a",
                          padding: "5px 20px",
                        }}
                      >
                        UPDATE DELIVERY FORMAT
                      </Button>
                    </>
                  )
                ) : (
                  <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}>
                    {deliveryFormatsList.map((dlf, index) => (
                      <ListItem alignItems="flex-start" key={dlf.id} sx={{ pl: 0 }}>
                        <ListItemText primary={dlf.text} secondary={dlf.value} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>

          {role === 3 && status == 10 && (
            <>
              <Box sx={{ overflow: "hidden", mt: 2 }}>
                <Paper className={classes.paper}>
                  <Grid container height="100%">
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          height: "100%",
                        }}
                      >
                        <h3 className={classes.detailsHeader}>
                          <b> Order </b>
                        </h3>

                        <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper", pt: 0, pb: 0 }}>
                          <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                            <ListItemText primary="Amount:" secondary={"$ " + amount} />
                          </ListItem>

                          <ListItem alignItems="flex-start" sx={{ pl: 0 }}>
                            <ListItemText primary="Description:" secondary={orderDescription} />
                          </ListItem>
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <HistoryTable
                columns={artworkHistoryColumns}
                history={artworkHistory}
                historyLoading={artworkHistoryLoading}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ViewJob;
